import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import MarketPriceTable from '@Types/entities/MarketPriceTable'
import MarketPriceSummary from '@Types/entities/MarketPriceSummary'
import { landAxios } from '@Root/utils/landAxios'
import MarketPriceChart from '@Types/entities/MarketPriceChart'
import { PROVIDER, TRADE } from '@Root/contants'
import MarketPrice from '@Types/entities/MarketPrice'
import RealPriceTable from '@Types/entities/RealPriceTable'

export const fetchChart = createAsyncThunk(
  'myhouse/fetchChart',
  async (params: MyhousePriceParam) =>
    await (
      await landAxios.get(`/complexes/${params.complexNo}/prices`, {
        params: { ...params, type: 'chart', complexNo: null },
      })
    ).data,
)

export const fetchTable = createAsyncThunk(
  'myhouse/fetchTable',
  async (params: MyhousePriceParam) =>
    await (
      await landAxios.get(`/complexes/${params.complexNo}/prices`, {
        params: { ...params, type: 'table', complexNo: null },
      })
    ).data,
)

export const fetchRealPriceTable = createAsyncThunk(
  'myhouse/fetchReal',
  async (params: MyhousePriceParam) =>
    await (
      await landAxios.get(`/complexes/${params.complexNo}/prices/real`, {
        params: { ...params, type: 'table', complexNo: null },
      })
    ).data,
)

export const fetchSummary = createAsyncThunk(
  'myhouse/fetchSummary',
  async (params: MyhousePriceParam) =>
    await (
      await landAxios.get(`/complexes/${params.complexNo}/prices`, {
        params: { ...params, type: 'summary', complexNo: null },
      })
    ).data,
)

export interface MyhousePriceParam {
  complexNo: number
  tradeType: TRADE
  priceChartChange: boolean
  areaNo: number
  year?: number
  provider?: PROVIDER
}

export interface MyhousePriceState {
  chart: MarketPriceChart
  table: MarketPriceTable
  summary: MarketPriceSummary
  real: RealPriceTable
  price: MarketPrice
  period: number
  tradeType: string
}

export const initialState: MyhousePriceState = {
  chart: {},
  table: {},
  summary: {},
  price: {},
  real: {},
  period: 3,
  tradeType: TRADE.RENT,
} as MyhousePriceState

export const myhousePriceSlice = createSlice({
  name: 'myhousePrice',
  initialState,
  reducers: {
    initializeState: (state: MyhousePriceState) => {
      Object.assign(state, initialState)
    },
    updatePeriod: (state: MyhousePriceState, action: PayloadAction<number>) => {
      state.period = action.payload
    },
    updateTradeType: (state: MyhousePriceState, action: PayloadAction<string>) => {
      state.tradeType = action.payload
    },
  },

  extraReducers: {
    [fetchChart.fulfilled.type]: (state: MyhousePriceState, action: PayloadAction<MarketPriceChart>) => {
      state.chart = action.payload
      state.chart.upperMarketPriceYList =
        action.payload.upperMarketPriceYList?.map((element, index) => {
          if (index === 0) {
            return element
          }
          return [element, element, action.payload.lowerMarketPriceYList[index]]
        }) || []
    },
    [fetchTable.fulfilled.type]: (state: MyhousePriceState, action: PayloadAction<MarketPriceTable>) => {
      state.table = action.payload
    },
    [fetchSummary.fulfilled.type]: (state: MyhousePriceState, action: PayloadAction<MarketPriceSummary>) => {
      state.summary = action.payload
    },
    [fetchRealPriceTable.fulfilled.type]: (state: MyhousePriceState, action: PayloadAction<RealPriceTable>) => {
      state.real = action.payload
    },
  },
})

export const { updateTradeType, updatePeriod, initializeState } = myhousePriceSlice.actions

export default myhousePriceSlice.reducer
